/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { OpPatch } from 'json-patch';
import { get, has, set } from 'json-pointer';
import { AjaxError } from "rxjs/ajax";
import { ActionType, createAction, createAsyncAction } from "typesafe-actions";
import { NIFormData, walkData } from '../../react/components/form/services';
import { DFSValueType, FormDefinitionWrapper, FormStatus } from '../../react/components/form/services/form';
import { parseFieldIdentifier, resolvePointer } from '../../react/components/form/services/processor';

export const UPDATE_EVALUATION = "DFS_EVAL_UPDATE";
export const DELETE_IMAGE = "DELETE_IMAGE";
export const UPLOAD_IMAGE = "UPLOAD_IMAGE";
export const UPLOAD_IMAGE_SUCCESS = "UPLOAD_IMAGE_SUCCESS";
export const UPLOAD_IMAGE_FAILURE = "UPLOAD_IMAGE_FAILURE";
export const DELAYED_UPLOAD_IMAGE = "DELAYED_UPLOAD_IMAGE";
export const DELAYED_UPLOAD_IMAGE_SUCCESS = "DELAYED_UPLOAD_IMAGE_SUCCESS";
export const DELAYED_UPLOAD_IMAGE_FAILURE = "DELAYED_UPLOAD_IMAGE_FAILURE";
export const STORE_LOCAL_IMAGE = "STORE_LOCAL_IMAGE";
export const STORE_LOCAL_IMAGE_SUCCESS = "STORE_LOCAL_IMAGE_SUCCESS";
export const STORE_LOCAL_IMAGE_FAILURE = "STORE_LOCAL_IMAGE_FAILURE";
export const DELETE_STALE_OFFLINE_IMAGE = "DELETE_STALE_OFFLINE_IMAGE";
export const DELETE_STALE_OFFLINE_IMAGE_SUCCESS = "DELETE_STALE_OFFLINE_IMAGE_SUCCESS";
export const DELETE_STALE_OFFLINE_IMAGE_FAILURE = "DELETE_STALE_OFFLINE_IMAGE_FAILURE";

/**
 * Everything but a form's definition
 */
export interface FormDescriptor {
  id: string;
  $schema?: string | undefined;
  form_id: string;
  version_program: number;
  version_major: number;
  version_minor: number;
  title: string;
  state: FormStatus;
  added: string;
  modified: string;
  short_title: string;
  desk_audit?: boolean;
  /**
   * App should prevent downloading of definitions unless it satisfies this version.
   */
  min_app_version: number;
}

export interface Action<T extends string, P> {
    type: T;
    payload?: P;
    error?: boolean;
}
export type FormUpdatePayload = OpPatch;

export interface DeleteImageParams {
    path: string;
    scope: string;
    toRemove: string;
    evalID: string;
    original: NIFormData;
}
export interface UploadImageParams {
    path: string;
    scope: string;
    evalID: string;
    id: string;
    uri: string;
}
export interface UploadSuccessParams {
    id: string;
    evalID: string;
}
// export declare const uploadImageActions: import("typesafe-actions").AsyncActionCreator<["UPLOAD_IMAGE", UploadImageParams], ["UPLOAD_IMAGE_SUCCESS", UploadSuccessParams], ["UPLOAD_IMAGE_FAILURE", Error], never, "UPLOAD_IMAGE", UploadImageParams, "UPLOAD_IMAGE_SUCCESS", UploadSuccessParams, "UPLOAD_IMAGE_FAILURE", Error, never, never>;
export interface StoreLocalImageParams {
    id: string;
    uri: string;
}
// export declare const storeLocalImageActions: import("typesafe-actions").AsyncActionCreator<["STORE_LOCAL_IMAGE", StoreLocalImageParams], ["STORE_LOCAL_IMAGE_SUCCESS", undefined], ["STORE_LOCAL_IMAGE_FAILURE", Error], never, "STORE_LOCAL_IMAGE", StoreLocalImageParams, "STORE_LOCAL_IMAGE_SUCCESS", undefined, "STORE_LOCAL_IMAGE_FAILURE", Error, never, never>;
export interface DelayedUploadImageParams {
    path: string;
    scope: string;
    evalID: string;
    id: string;
    uri: string;
}
export interface DelayedUploadSuccessParams {
    evalID: string;
    id: string;
}
// export declare const delayedUploadImageActions: import("typesafe-actions").AsyncActionCreator<["DELAYED_UPLOAD_IMAGE", DelayedUploadImageParams], ["DELAYED_UPLOAD_IMAGE_SUCCESS", DelayedUploadSuccessParams], ["DELAYED_UPLOAD_IMAGE_FAILURE", Error], never, "DELAYED_UPLOAD_IMAGE", DelayedUploadImageParams, "DELAYED_UPLOAD_IMAGE_SUCCESS", DelayedUploadSuccessParams, "DELAYED_UPLOAD_IMAGE_FAILURE", Error, never, never>;
export interface UploadImageSuccessParams {
    path: string;
    scope: string;
    evalID: string;
    oldImageID: string;
    newImageID: string;
    uri: string;
}
// export declare const deleteStaleOfflineImageActions: import("typesafe-actions").AsyncActionCreator<["DELETE_STALE_OFFLINE_IMAGE", DeleteStaleOfflineImageParams], ["DELETE_STALE_OFFLINE_IMAGE_SUCCESS", undefined], ["DELETE_STALE_OFFLINE_IMAGE_FAILURE", Error], never, "DELETE_STALE_OFFLINE_IMAGE", DeleteStaleOfflineImageParams, "DELETE_STALE_OFFLINE_IMAGE_SUCCESS", undefined, "DELETE_STALE_OFFLINE_IMAGE_FAILURE", Error, never, never>;
export interface DeleteStaleOfflineImageParams {
    oldID: string;
}
export type EvaluationUpdateAction = ActionType<typeof updateForm>;
export type UploadImageActions = ActionType<typeof uploadImageActions>;
export type UploadImageSuccessAction = ActionType<typeof uploadImageSuccess>;
export type DelayedUploadImageAction = ActionType<typeof delayedUploadImageActions>;
export type StoreLocalImageActions = ActionType<typeof storeLocalImageActions>;
export type DeleteStaleOfflineImageActions = ActionType<typeof deleteStaleOfflineImageActions>;
export type ImageActions = UploadImageActions | UploadImageSuccessAction | DelayedUploadImageAction | StoreLocalImageActions | DeleteStaleOfflineImageActions;

export const updateForm = createAction(UPDATE_EVALUATION, e => {
  return function(t: { payload: OpPatch, evalID: string }) {
    const a = t.payload;
    const o = t.evalID;

    return e(a, o);
  }
})

/**
 * @returns the current value stored for the given field name. If the field is not present, returns undefined.
 */
 export function getFormValue(encodedPath: string, data: NIFormData, scope: string = '') {

  const  i = resolvePointer(parseFieldIdentifier(encodedPath), scope);
  if (has(data, i)) {
    return get(data, i)
  }

  return undefined;
}

/**
 * Crawl through evaluation data to determine correct path + value based on whether target path is an array item or not
 * 
 * @returns an updateForm action
 */
 export function setFormValue(path: string, value: any, currentData: any, evalID: string) {
  let r;
  let inspectPath = parseFieldIdentifier(path);
  let finalValue = value;

  while (inspectPath !== '') {
      const parentPathEndBound = inspectPath.lastIndexOf('/') || 0;
      const parentPath = inspectPath.slice(0, parentPathEndBound);

      if (has(currentData, parentPath)) {
        break;
      }

      const lastComponent = inspectPath.slice(parentPathEndBound + 1);

      finalValue = isNaN(+lastComponent) && lastComponent !== '-'
        ? { [lastComponent]: finalValue }
        : [ finalValue ];
      inspectPath = parentPath;
  }

  return updateForm({
      payload: {
        op: 'add',
        path: inspectPath,
        value: finalValue
      },
      evalID: evalID
  })
}

export function replaceFormValue(path: string, value: DFSValueType, evalID: string) {
  return updateForm({
    evalID: evalID,
    payload: {
      op: 'replace',
      path: parseFieldIdentifier(path),
      value: value
    }
  })
}

export function removeFormValue(path: string, evalID: string) {
    var a = parseFieldIdentifier(path);

    return updateForm({
        payload: {
          op: 'remove',
          path: a
        },
        evalID: evalID
    })
}

export const deleteImage: (params: DeleteImageParams) => {
  type: "DELETE_IMAGE";
  payload: DeleteImageParams;
} = createAction(DELETE_IMAGE, function(e) {
    return function(t) {
        return e(t)
    }
});

/**
 * Generates the initial patch for a given schema
 */
export function computeInitialState(form: FormDefinitionWrapper) {
  const e = {};
  const i = /properties\//g;

  walkData(form.definition.data, function r(t, a) {

      if (a === '') {
        return;
      }

      const s = a.replace(i, '');

      if (t.properties && !has(e, s)) {
        set(e, s, {})
      } else if (t.items && !has(e, s)) {
        set(e, s, [])
      } else if (t.default !== undefined) {
        set(e, a.replace(i, ''), t.default)
      }
      if (t.allOf) {
          for (const u of t.allOf) {
            walkData(u, r, a)
          }
      }
      if (t.anyOf) {
        for (const u of t.anyOf) {
          walkData(u, r, a)
        }
      }
      if (t.then) {
        walkData(t.then, r, a)
      }
  });

  return e;
};

export const uploadImageActions = createAsyncAction(UPLOAD_IMAGE, UPLOAD_IMAGE_SUCCESS, UPLOAD_IMAGE_FAILURE)<UploadImageParams, UploadSuccessParams, Error>();
export const storeLocalImageActions = createAsyncAction(STORE_LOCAL_IMAGE, STORE_LOCAL_IMAGE_SUCCESS, STORE_LOCAL_IMAGE_FAILURE)<StoreLocalImageParams, undefined, Error>();
export const delayedUploadImageActions = createAsyncAction(DELAYED_UPLOAD_IMAGE, DELAYED_UPLOAD_IMAGE_SUCCESS, DELAYED_UPLOAD_IMAGE_FAILURE)<DelayedUploadImageParams, DelayedUploadSuccessParams, Error>();
export const uploadImageSuccess = createAction(UPLOAD_IMAGE_SUCCESS, function(e) {
  return function(t: UploadImageSuccessParams) {
    return e(t)
  }
});
export const deleteStaleOfflineImageActions = createAsyncAction(DELETE_STALE_OFFLINE_IMAGE, DELETE_STALE_OFFLINE_IMAGE_SUCCESS, DELETE_STALE_OFFLINE_IMAGE_FAILURE)<DeleteStaleOfflineImageParams, undefined, Error>();


export const fetchFormsActions = createAsyncAction("FETCH_FORMS_REQUEST", "FETCH_FORMS_SUCCESS", "FETCH_FORMS_FAILURE")<
  undefined,
  FormDescriptor[],
  AjaxError
>();

export const fetchFormActions = createAsyncAction("FETCH_FORM_REQUEST", "FETCH_FORM_SUCCESS", "FETCH_FORM_FAILURE")<
  string,
  FormDefinitionWrapper,
  AjaxError
>();

export const saveFormActions = createAsyncAction("SAVE_FORM_REQUEST", "SAVE_FORM_SUCCESS", "SAVE_FORM_FAILURE")<
  FormDefinitionWrapper,
  string,
  Error
>();

export type FormMap = {
  [id: string]: { [key: number]: FormDefinitionWrapper };
};
export const loadFormsActions = createAsyncAction("LOAD_FORMS_REQUEST", "LOAD_FORMS_SUCCESS", "LOAD_FORMS_FAILURE")<
  undefined,
  FormMap,
  Error
>();

export const loadFormActions = createAsyncAction("LOAD_FORM_REQUEST", "LOAD_FORM_SUCCESS", "LOAD_FORM_FAILURE")<
  { type: string; version: number },
  FormDefinitionWrapper,
  Error
>();

export const removedInaccessibleForms = createAction("REMOVED_INACCESSIBLE_FORMS");
export const throttledSyncForms = createAction('THROTTLED_SYNC_FORMS');
export const setActiveDefinition = createAction('SET_ACTIVE_DEFINITION', (action) => (definition: FormDescriptor | null) => action(definition));

export type FormDefinitionActions =
  | ActionType<typeof fetchFormsActions>
  | ActionType<typeof fetchFormActions>
  | ActionType<typeof saveFormActions>
  | ActionType<typeof loadFormsActions>
  | ActionType<typeof loadFormActions>
  | ActionType<typeof removedInaccessibleForms>
  | ActionType<typeof throttledSyncForms>
  | ActionType<typeof setActiveDefinition>;
